import {
  TARGET_RATIO_LABELS,
  TARGET_RATIO_ORDER,
  targetRatiosMatchDefault,
  type TargetRatio,
  type TargetRatioPlatformFamily,
} from "@nexus/contracts"
import { Badge, Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"

const RATIO_SHAPE_CLASS: Record<TargetRatio, string> = {
  "16:9": "w-[30px] h-[17px]",
  "9:16": "w-[15px] h-[26px]",
  "4:5": "w-5 h-[25px]",
  "1:1": "size-[22px]",
}

export interface TargetRatiosPickerProps {
  family: TargetRatioPlatformFamily
  selected: readonly TargetRatio[]
  onToggle: (ratio: TargetRatio) => void
  isReady: boolean
  showEmptyError?: boolean
  disabled?: boolean
}

export function TargetRatiosPicker({
  family,
  selected,
  onToggle,
  isReady,
  showEmptyError,
  disabled = false,
}: TargetRatiosPickerProps) {
  const { t } = useTranslation()
  const showError = showEmptyError ?? (isReady && selected.length === 0)
  const matchesDefault = isReady && targetRatiosMatchDefault(selected, family)
  const defaultSuffix = matchesDefault
    ? family === "youtube"
      ? t("marketplace.sendHandoff.youtubeDefaultSuffix", " · YouTube default")
      : t("marketplace.sendHandoff.metaDefaultSuffix", " · Meta default")
    : ""
  const platformName =
    family === "youtube"
      ? t("marketplace.sendHandoff.platformYouTube", "YouTube")
      : family === "meta"
        ? t("marketplace.sendHandoff.platformMeta", "Meta")
        : null

  return (
    <div className="flex flex-col gap-3">
      {platformName ? (
        <p className="text-xs text-muted-foreground">
          {t("marketplace.sendHandoff.platformLine", {
            platform: platformName,
            defaultValue: "Platform: {{platform}}",
          })}
        </p>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-medium text-foreground">
            {t("marketplace.sendHandoff.targetRatiosTitle", "Target ratios")}
          </h3>
          <Badge variant="slate">
            {t("marketplace.sendHandoff.perOrder", "Per order")}
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground">
          {t("marketplace.sendHandoff.ratiosSelected", {
            count: selected.length,
            total: TARGET_RATIO_ORDER.length,
            defaultValue: "{{count}} of {{total}} selected",
          })}
          {defaultSuffix}
        </p>
      </div>
      {showError ? (
        <p
          role="alert"
          className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-foreground"
        >
          {t(
            "marketplace.sendHandoff.targetRatiosRequired",
            "Select at least one ratio so the partner knows which cuts to deliver.",
          )}
        </p>
      ) : family === "youtube" && isReady ? (
        <p className="text-xs text-muted-foreground">
          {t(
            "marketplace.sendHandoff.youtubeRatioHelper",
            "All four are pre-selected for YouTube. Untick any ratio the partner should not deliver.",
          )}
        </p>
      ) : family === "meta" && isReady ? (
        <p className="text-xs text-muted-foreground">
          {t(
            "marketplace.sendHandoff.metaRatioHelper",
            "Meta default covers Reels/Stories (9:16), Feed (4:5) and Square (1:1). Tick 16:9 if you also need a landscape cut.",
          )}
        </p>
      ) : null}
      <div
        role="group"
        aria-label={t("marketplace.sendHandoff.targetRatiosTitle", "Target ratios")}
        className="grid grid-cols-4 gap-2"
      >
        {TARGET_RATIO_ORDER.map((ratio) => {
          const isSelected = selected.includes(ratio)
          const label = t(
            `marketplace.sendHandoff.ratio${ratioLabelKey(ratio)}`,
            TARGET_RATIO_LABELS[ratio],
          )
          return (
            <button
              key={ratio}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              aria-label={label}
              disabled={disabled || !isReady}
              onClick={() => onToggle(ratio)}
              className={cn(
                "relative flex min-h-[7.5rem] flex-col items-center justify-end gap-1 rounded-lg border px-2 pb-3 pt-7",
                isSelected
                  ? "border-primary bg-primary/10"
                  : "border-input bg-background",
                showError && "border-destructive",
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "absolute right-2 top-2 flex size-4 items-center justify-center rounded-[3px] border",
                  isSelected
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-input bg-background",
                )}
              >
                {isSelected ? <Icon name="check" size="xs" color="primaryForeground" /> : null}
              </span>
              <span
                aria-hidden
                className={cn(
                  "mb-2 rounded-[2px] border",
                  RATIO_SHAPE_CLASS[ratio],
                  isSelected
                    ? "border-primary bg-primary/20"
                    : "border-muted-foreground/40",
                )}
              />
              <span className="text-xs font-medium text-foreground">{ratio}</span>
              <span className="text-[11px] text-muted-foreground">{label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ratioLabelKey(ratio: TargetRatio): string {
  if (ratio === "16:9") return "Landscape"
  if (ratio === "9:16") return "Vertical"
  if (ratio === "4:5") return "Portrait"
  return "Square"
}

export function TargetRatioOrderSummary({
  scriptCount,
  ratioCount,
}: {
  scriptCount: number
  ratioCount: number
}) {
  const { t } = useTranslation()
  const scripts = t("marketplace.sendHandoff.summaryScripts", {
    count: scriptCount,
    defaultValue: "{{count}} scripts",
  })
  const ratios =
    ratioCount === 0
      ? t("marketplace.sendHandoff.summaryNoRatios", "no ratios")
      : t("marketplace.sendHandoff.summaryRatios", {
          count: ratioCount,
          defaultValue: "{{count}} ratios",
        })

  return (
    <div className="min-w-0 text-left">
      <p className="text-xs text-muted-foreground">
        {t("marketplace.sendHandoff.orderSummary", "Order summary")}
      </p>
      <p className="text-sm text-foreground">
        {scripts}
        {" · "}
        {ratios}
      </p>
    </div>
  )
}
